import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { formatNumber } from '@awj/i18n';

import { AccountMenu } from './AccountMenu.tsx';
import { Icon } from './Icon.tsx';
import { LocaleSwitch } from './LocaleSwitch.tsx';
import { NavIcon } from './NavIcon.tsx';
import { trailFor, visibleGroups } from './navigation.ts';
import { useI18n } from '~/i18n/index.tsx';
import { useDashboard, useInquiryCounts, usePendingReviewCount } from '~/lib/queries.ts';
import { useSession } from '~/lib/session.tsx';
import { storefrontPage } from '~/lib/storefront.ts';

/**
 * The shell: a fixed rail, a sticky command bar, and one scrolling page column.
 *
 * The markup is the reference panel's, element for element and class for class — `.app`,
 * `.sidebar`, `.topbar`, `.app__container` — so the stylesheets carried over in `styles/ref`
 * paint it without a single rule having to be adapted. The data behind it is AWJ's.
 *
 * The rail is the panel's own: the shop's name at the head on the command bar's line, and the
 * pages under it with a count beside any that has work waiting.
 *
 * The command bar reads in three parts: where you are (the cluster and the page, the page a way
 * back to its list from anything under it), the two controls about the panel rather than a page
 * (the shop, the language) in one tray, and who you are, with the way out behind it.
 */
export function AdminLayout() {
  const { t, locale } = useI18n();
  const location = useLocation();
  const { has } = useSession();

  const groups = visibleGroups(has);
  const trail = trailFor(location.pathname);
  // On the list itself the trail ends at the page; under it (an order, a product) it leads back.
  const onList = trail?.item.to === location.pathname;

  /*
   * What is waiting on someone, beside the page that holds it — the same three queues the
   * dashboard leads with. The shell stays mounted across navigation, so these are read once and
   * then kept fresh by the invalidations every edit already makes, not once per page.
   */
  const dashboard = useDashboard();
  const reviews = usePendingReviewCount();
  const inquiries = useInquiryCounts();
  const waiting: Record<string, number | undefined> = {
    '/orders': dashboard.data?.awaitingFulfilment,
    '/reviews': reviews.data,
    '/inquiries': inquiries.data?.New,
  };

  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawer = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = drawer.current;

    if (!dialog) {
      return;
    }

    if (drawerOpen && !dialog.open) {
      dialog.showModal();
    } else if (!drawerOpen && dialog.open) {
      dialog.close();
    }
  }, [drawerOpen]);

  // Navigating away closes the drawer, or the route changes underneath an open menu.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  /*
   * The same list, rendered twice — once in the rail and once in the drawer. Only one is ever
   * displayed, but they are given distinct names anyway: two landmarks called "Main navigation"
   * is confusing to anyone browsing by landmark, and the cost of distinguishing them is a word.
   */
  const navigation = (label: string) => (
    <nav className="sidebar__nav" aria-label={label}>
      {groups.map((group, index) => (
        // No caption over the cluster: the hairline between clusters is the grouping, and the
        // cluster's name is said once, in the command bar, for the page you are on.
        // eslint-disable-next-line react/no-array-index-key -- a label-less cluster has no id
        <div className="sidebar__group" key={group.label ?? index}>
          {group.items.map((item) => {
            const count = waiting[item.to];

            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`}
              >
                <span className="sidebar__icon">
                  <NavIcon name={item.icon} />
                </span>
                <span className="sidebar__label">{t(item.label)}</span>
                {/* Only when there is something to do: a row of zeros is a row of noise. */}
                {count ? (
                  <span className="sidebar__count">
                    {formatNumber(locale, count)}
                    <span className="sr-only"> {t('nav.waiting')}</span>
                  </span>
                ) : null}
              </NavLink>
            );
          })}
        </div>
      ))}
    </nav>
  );

  // The head, shared by the rail and the drawer: the house lockup, the same artwork the shop's
  // header carries, drawn as a mask so it takes the rail's white.
  const brand = (
    <div className="sidebar__brand">
      <span className="admin__wordmark" role="img" aria-label={t('app.title')} />
    </div>
  );

  return (
    <div className="app">
      <a className="skip-link" href="#admin-content">
        {t('a11y.skipToContent')}
      </a>

      <aside className="sidebar">
        {brand}
        {navigation(t('nav.primary'))}
      </aside>

      <div className="app__main">
        <header className="topbar">
          <div className="topbar__start">
            <button
              type="button"
              className="topbar__burger"
              onClick={() => setDrawerOpen(true)}
              aria-label={t('nav.primary')}
              aria-expanded={drawerOpen}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M4 7h16M4 12h16M4 17h16"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            {trail ? (
              <nav className="crumbs" aria-label={t('nav.breadcrumb')}>
                <span className="crumbs__icon" aria-hidden="true">
                  <NavIcon name={trail.item.icon} />
                </span>
                <ol className="crumbs__list">
                  {trail.group ? <li className="crumbs__step">{t(trail.group)}</li> : null}
                  <li className="crumbs__step crumbs__step--page">
                    {trail.group ? (
                      <Icon name="chevronEnd" size={14} className="crumbs__sep rtl-flip" />
                    ) : null}
                    {onList ? (
                      <span aria-current="page">{t(trail.item.label)}</span>
                    ) : (
                      <Link className="crumbs__link" to={trail.item.to}>
                        {t(trail.item.label)}
                      </Link>
                    )}
                  </li>
                </ol>
              </nav>
            ) : null}
          </div>

          <div className="topbar__end">
            <div className="topbar__tools">
              {/* The shop as customers see it, in the language the panel is in, in a tab of its own. */}
              <a
                className="topbar__shop"
                href={storefrontPage(locale, '/')}
                target="_blank"
                rel="noreferrer"
              >
                <Icon name="external" size={16} />
                <span className="topbar__shop-label">{t('nav.viewShop')}</span>
                <span className="sr-only"> ({t('common.opensInNewTab')})</span>
              </a>
              <LocaleSwitch />
            </div>

            <AccountMenu />
          </div>
        </header>

        <div className="app__content">
          <main className="app__container" id="admin-content">
            <Outlet />
          </main>
        </div>
      </div>

      <dialog
        ref={drawer}
        className="sidebar-drawer"
        onClose={() => setDrawerOpen(false)}
        onClick={(event) => {
          if (event.target === drawer.current) {
            setDrawerOpen(false);
          }
        }}
      >
        <div className="sidebar sidebar--drawer">
          {brand}
          {navigation(t('nav.sections'))}
        </div>
      </dialog>
    </div>
  );
}
